import { useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import {
  formatMDY,
  formatMDYWithTime,
  getStatusColor,
  isFutureRequest,
  isPastWeekRequest,
} from "@utils/ptoHelpers";
import { useTranslation } from "react-i18next";

export function PTOTableViewMobile({
  requests,
  isLoading,
  approveMutation,
  handleReject,
  cancelMutation,
  searchVal,
  userType,
}: {
  requests: any[];
  isLoading: boolean;
  approveMutation: any;
  cancelMutation: any;
  handleReject: (id: number) => void;
  searchVal?: string;
  userType?: string;
}) {
  const { t } = useTranslation();
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    action: "approve" | "reject" | "cancel" | null;
    request: any;
  }>({ isOpen: false, action: null, request: null });

  const filteredRequests = useMemo(() => {
    if (!searchVal || !requests) return requests || [];

    const lowerSearch = searchVal.toLowerCase();
    return requests.filter((r: any) => {
      const contractorName = (r.contractor_name || "").toLowerCase();
      const userEmail = (r.user_email || "").toLowerCase();
      const userId = String(r.user_id || "").toLowerCase();
      const status = (r.status || "").toLowerCase();
      const requestType = (r.request_type || "").toLowerCase();
      const description = (r.description || "").toLowerCase();
      const statusReason = (r.status_reason || "").toLowerCase();

      return (
        contractorName.includes(lowerSearch) ||
        userEmail.includes(lowerSearch) ||
        userId.includes(lowerSearch) ||
        status.includes(lowerSearch) ||
        requestType.includes(lowerSearch) ||
        description.includes(lowerSearch) ||
        statusReason.includes(lowerSearch)
      );
    });
  }, [requests, searchVal]);

  const handleConfirmAction = () => {
    if (!confirmModal.request || !confirmModal.action) return;

    const { id } = confirmModal.request;
    switch (confirmModal.action) {
      case "approve":
        approveMutation.mutate({ id });
        break;
      case "reject":
        handleReject(id);
        break;
      case "cancel":
        cancelMutation.mutate({ id });
        break;
    }
    setConfirmModal({ isOpen: false, action: null, request: null });
  };

  if (isLoading) {
    return <div className="tw-p-4 tw-text-gray-500">{t("pto.loading")}</div>;
  }

  if (!filteredRequests || filteredRequests.length === 0) {
    return (
      <div className="tw-p-4 tw-text-center tw-text-gray-500">
        {searchVal ? t("pto.noMatchingRequests") : t("pto.noRequests")}
      </div>
    );
  }

  return (
    <>
      <div className="tw-space-y-3">
        {filteredRequests.map((r) => (
          <div
            key={r.id}
            className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-shadow-sm"
          >
            {/* Header */}
            <div className="tw-flex tw-justify-between tw-items-start tw-mb-3">
              <div className="tw-flex-1">
                <div className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-bg-blue-50 tw-px-2 tw-py-1 tw-rounded tw-inline-block">
                  {r.contractor_name || r.user_email || `ID: ${r.user_id}`}
                </div>
                <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                  Requested: {formatMDYWithTime(r.request_date)}
                </div>
              </div>
              <div
                className={`tw-text-sm tw-capitalize ${getStatusColor(
                  r.status,
                  r.request_type
                )}`}
              >
                {r.status}
              </div>
            </div>

            {/* Details */}
            <div className="tw-space-y-2 tw-text-sm tw-border-t tw-border-gray-100 tw-pt-3">
              {r.contractor_id && (
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">Contractor ID:</span>
                  <span className="tw-font-medium">{r.contractor_id}</span>
                </div>
              )}

              <div className="tw-flex tw-justify-between">
                <span className="tw-text-gray-600">Dates:</span>
                <span className="tw-font-medium">
                  {formatMDY(r.start_date)} - {formatMDY(r.end_date)}
                </span>
              </div>

              <div className="tw-flex tw-justify-between">
                <span className="tw-text-gray-600">Hours:</span>
                <span className="tw-font-medium">{r.hours_requested}</span>
              </div>

              {r.request_type && (
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">Type:</span>
                  <span className="tw-capitalize">{r.request_type}</span>
                </div>
              )}

              {r.pto_balance_at_request != null && (
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">Bal@Req:</span>
                  <span>{r.pto_balance_at_request}</span>
                </div>
              )}

              {r.pto_balance_after != null && (
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">Bal After:</span>
                  <span>{r.pto_balance_after}</span>
                </div>
              )}

              {r.temp_number && (
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">Temp #:</span>
                  <span>{r.temp_number}</span>
                </div>
              )}

              {r.second_name && (
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">Second Name:</span>
                  <span>{r.second_name}</span>
                </div>
              )}

              {r.fourth_name && (
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-600">Fourth Name:</span>
                  <span>{r.fourth_name}</span>
                </div>
              )}

              {r.description && (
                <div className="tw-mt-2">
                  <span className="tw-text-gray-600">Description:</span>
                  <p className="tw-text-gray-800 tw-mt-1">{r.description}</p>
                </div>
              )}

              {r.status_reason && (
                <div className="tw-mt-2">
                  <span className="tw-text-gray-600">Note:</span>
                  <p className="tw-text-gray-800 tw-mt-1">{r.status_reason}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="tw-flex tw-gap-2 tw-mt-4 tw-pt-3 tw-border-t tw-border-gray-100">
              <button
                className="tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-text-green-700 tw-bg-green-100 hover:tw-bg-green-200 tw-border tw-border-green-200 tw-rounded tw-px-2 tw-py-2 disabled:tw-text-gray-500 disabled:tw-bg-gray-100 disabled:tw-border-gray-300 disabled:tw-cursor-not-allowed tw-text-sm"
                onClick={() =>
                  setConfirmModal({
                    isOpen: true,
                    action: "approve",
                    request: r,
                  })
                }
                disabled={r.status !== "Pending" || approveMutation.isPending}
                title="Approve"
              >
                <Check size={16} /> Approve
              </button>

              <button
                className="tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-text-red-700 tw-bg-red-100 hover:tw-bg-red-200 tw-border tw-border-red-200 tw-rounded tw-px-2 tw-py-2 disabled:tw-text-gray-500 disabled:tw-bg-gray-100 disabled:tw-border-gray-300 disabled:tw-cursor-not-allowed tw-text-sm"
                onClick={() =>
                  setConfirmModal({
                    isOpen: true,
                    action: "reject",
                    request: r,
                  })
                }
                disabled={r.status !== "Pending"}
                title="Reject"
              >
                <X size={16} /> Reject
              </button>

              <button
                className="tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-text-orange-700 tw-bg-orange-100 hover:tw-bg-orange-200 tw-border tw-border-orange-200 tw-rounded tw-px-2 tw-py-2 disabled:tw-text-gray-500 disabled:tw-bg-gray-100 disabled:tw-border-gray-300 disabled:tw-cursor-not-allowed tw-text-sm"
                onClick={() =>
                  setConfirmModal({
                    isOpen: true,
                    action: "cancel",
                    request: r,
                  })
                }
                disabled={
                  !(
                    r.status === "Approved" || r.status === "Balance_Overridden"
                  ) ||
                  (userType === "super_admin"
                    ? !(isFutureRequest(r) || isPastWeekRequest(r))
                    : !isFutureRequest(r)) ||
                  cancelMutation.isPending
                }
              >
                <X size={16} /> Cancel
              </button>
            </div>
          </div>
        ))}
      </div>

      {confirmModal.isOpen && confirmModal.request && (
        <div
          className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-2 tw-bg-black/50"
          onClick={() =>
            setConfirmModal({ isOpen: false, action: null, request: null })
          }
        >
          <div
            className="tw-bg-white dark:tw-bg-[#0D1117] tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-shadow-xl tw-max-w-xs tw-w-full tw-p-3 tw-max-h-[90vh] tw-overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="tw-text-sm tw-font-semibold tw-mb-2">
              Confirm{" "}
              {confirmModal.action
                ? confirmModal.action.charAt(0).toUpperCase() +
                  confirmModal.action.slice(1)
                : ""}
            </h3>
            <p className="tw-text-xs tw-text-gray-600 tw-mb-2">
              Are you sure you want to {confirmModal.action} this PTO request?
            </p>
            <div className="tw-bg-gray-50 dark:tw-bg-gray-800 tw-rounded tw-p-2 tw-mb-2">
              <div className="tw-text-xs sm:tw-text-sm">
                <span className="tw-font-semibold">Contractor: </span>
                {confirmModal.request.contractor_name ||
                  confirmModal.request.user_email ||
                  confirmModal.request.user_id}
              </div>
              <div className="tw-text-xs sm:tw-text-sm tw-text-gray-600 tw-mt-1">
                <span className="tw-font-semibold">Dates: </span>
                {formatMDY(confirmModal.request.start_date)} -{" "}
                {formatMDY(confirmModal.request.end_date)}
              </div>
              <div className="tw-text-xs sm:tw-text-sm tw-text-gray-600">
                <span className="tw-font-semibold">Hours: </span>
                {confirmModal.request.hours_requested}
              </div>
            </div>
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-justify-end">
              <button
                className="tw-w-full sm:tw-w-auto tw-px-3 tw-py-1 tw-border tw-border-gray-300 tw-rounded tw-text-xs tw-text-gray-700 hover:tw-bg-gray-100"
                onClick={() =>
                  setConfirmModal({
                    isOpen: false,
                    action: null,
                    request: null,
                  })
                }
              >
                Cancel
              </button>
              <button
                className={`tw-w-full sm:tw-w-auto tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-text-white ${
                  confirmModal.action === "approve"
                    ? "tw-bg-green-600 hover:tw-bg-green-700"
                    : confirmModal.action === "reject"
                    ? "tw-bg-red-600 hover:tw-bg-red-700"
                    : "tw-bg-orange-600 hover:tw-bg-orange-700"
                }`}
                onClick={handleConfirmAction}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
